import React, { useEffect, useState } from 'react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Separator } from '@fluentui/react/lib/Separator';
import { TextField } from '@fluentui/react/lib/TextField';
import { Dropdown, IconButton, IDropdownOption, Label, Link, Text } from '@fluentui/react';
import { Video } from '../../../../../api/generated/data-contracts';
import DOMPurify from 'dompurify';

interface propsAdminVideo {
    Video: Video;
    ReorderVideo: Function;
    RemoveVideo: Function;
    VideoUpdated: Function;
}

const options: IDropdownOption[] = [
    { key: '', text: 'Select a source' },
    { key: 'youtube', text: 'Youtube' },
    { key: 'msstream', text: 'MS Stream' },
];

type ExtractResult = {
    src: string;
    error: string;
};

/**
 * Extracts and validates the src URL from a pasted iframe embed string.
 * - Sanitizes the input to only allow <iframe src="...">
 * - Parses with DOMParser to locate the iframe element
 * - Checks that the src is a well-formed absolute URL
 */
function extractIframeSrc(embedHtml: string): ExtractResult {
    const trimmed = embedHtml.trim();
    if (!trimmed) {
        return { src: '', error: '' };
    }

    const clean = DOMPurify.sanitize(embedHtml, {
        ALLOWED_TAGS: ['iframe'],
        ALLOWED_ATTR: ['src'],
    });
    const doc = new DOMParser().parseFromString(clean, 'text/html');
    const iframe = doc.querySelector('iframe');
    if (!iframe) {
        return { src: '', error: 'No valid <iframe> found.' };
    }

    const raw = iframe.getAttribute('src') || '';
    try {
        // check that raw is a valid URL
        new URL(raw);
    } catch {
        return { src: '', error: 'Invalid URL in iframe src.' };
    }

    return { src: raw, error: '' };
}

function AdminVideo(props: propsAdminVideo) {
    const [video, setVideo] = useState<Video>(null);
    const [videoHeader, setVideoHeader] = useState<string>(props.Video.header);
    const [videoDescription, setVideoDescription] = useState<string>(props.Video.description);
    const [videoOrder, setVideoOrder] = useState<number>(props.Video.order);

    // rawEmbed holds the full <iframe>…</iframe> HTML that the user edits,
    // cleanSrc holds the single validated URL sent to the backend,
    // embedError holds any parsing/validation error to display.
    const [rawEmbed, setRawEmbed] = useState<string>('');
    const [cleanSrc, setCleanSrc] = useState<string>(props.Video.url);
    const [embedError, setEmbedError] = useState<string>('');

    const [headerInFocus, setHeaderInFocus] = useState<boolean>(false);
    const headertxtfield = React.useRef(null);

    useEffect(() => {
        setVideo(props.Video);
        setVideoHeader(props.Video.header);
        setVideoOrder(props.Video.order);

        // On mount or when Video prop changes,
        // if the stored URL is already an iframe snippet, use it;
        // otherwise wrap legacy URL in a basic iframe for editing.
        const url = props.Video.url.trim();
        let html = '';
        if (url === '') {
            html = '';
        } else if (url.startsWith('<iframe')) {
            html = props.Video.url;
        } else {
            html = `<iframe src="${props.Video.url}" width="560" height="315" frameborder="0" allowfullscreen></iframe>`;
        }

        setRawEmbed(html);

        const { src, error } = extractIframeSrc(html);
        setCleanSrc(src || props.Video.url);
        setEmbedError(error);
    }, [props.Video]);

    const moveUpClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement, MouseEvent>) => {
        props.ReorderVideo(video, -1);
    };
    const moveDownClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement, MouseEvent>) => {
        props.ReorderVideo(video, 1);
    };
    const removeClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement, MouseEvent>) => {
        props.RemoveVideo(video, 1);
    };
    const newUpdateVideo = () => {
        props.VideoUpdated(video.id, videoHeader, videoDescription, cleanSrc);
    };
    const updatevideoHeader = (event, value) => {
        setVideoHeader(value);
        // props.videoUpdated(video.id, videoHeader, videoEmail, videoPhone);
    };
    const videoDescriptionUpdate = (event, value) => {
        setVideoDescription(value);
        // props.videoUpdated(video.id, videoHeader, videoEmail, videoPhone);
    };
    const videoEmbedChange = (event, value) => {
        setRawEmbed(value || '');
    };
    const videoEmbedBlur = () => {
        const { src, error } = extractIframeSrc(rawEmbed);
        setEmbedError(error);
        if (src) {
            setCleanSrc(src);
            props.VideoUpdated(video.id, videoHeader, videoDescription, src);
        }
    };
    useEffect(() => {
        if (headerInFocus === true) {
            headertxtfield.current.focus();
        }
    }, [headerInFocus]);
    return (
        <div style={{ width: 660 }}>
            <Stack tokens={{ childrenGap: 20 }} horizontal verticalAlign="baseline">
                <Stack.Item grow={1}>
                    {headerInFocus ? (
                        <TextField
                            componentRef={headertxtfield}
                            onBlur={() => {
                                setHeaderInFocus(false);
                                newUpdateVideo();
                            }}
                            value={videoHeader}
                            onChange={updatevideoHeader}
                        />
                    ) : (
                        <Stack horizontal tokens={{ childrenGap: 0 }} verticalAlign="baseline">
                            <h4
                                onClick={() => {
                                    setHeaderInFocus(true);
                                }}
                                style={{ cursor: 'pointer', textDecoration: 'none' }}
                            >
                                {videoHeader}
                            </h4>
                        </Stack>
                    )}
                </Stack.Item>
                <Stack.Item grow={5}>
                    <Separator></Separator>
                </Stack.Item>
                <Stack.Item grow={1} styles={{ root: { alignItems: 'center', display: 'flex', justifyContent: 'flex-end' } }}>
                    <Stack tokens={{ childrenGap: 0 }} horizontal>
                        <IconButton
                            iconProps={{ iconName: 'SkypeCircleArrow' }}
                            checked={false}
                            style={{ fontSize: 32, color: 'var(--palette-black)', transform: 'rotate(-90deg)' }}
                            onClick={moveDownClick}
                        />
                        <IconButton
                            iconProps={{ iconName: 'SkypeCircleArrow' }}
                            checked={false}
                            style={{ fontSize: 32, color: 'var(--palette-black)', transform: 'rotate(90deg)' }}
                            onClick={moveUpClick}
                        />
                        <IconButton
                            iconProps={{ iconName: 'SkypeCircleMinus' }}
                            checked={false}
                            style={{ fontSize: 32, color: 'var(--palette-black)' }}
                            onClick={removeClick}
                        />
                    </Stack>
                </Stack.Item>
            </Stack>

            <div>
                <Stack tokens={{ childrenGap: 10 }} verticalAlign="start" style={{ margin: 10 }}>
                    <TextField width={300} placeholder={'Description'} value={videoDescription} onChange={videoDescriptionUpdate} onBlur={newUpdateVideo} />
                    <TextField
                        width={300}
                        placeholder="Embed code"
                        value={rawEmbed}
                        onChange={videoEmbedChange}
                        onBlur={videoEmbedBlur}
                        errorMessage={embedError}
                        multiline
                        autoAdjustHeight
                    />
                    <Text variant="small">
                        Help finding the embed code?{' '}
                        <Link href="https://projectumsupport.freshdesk.com/a/solutions/articles/80001178698?lang=en&portalId=80000036179" target="_blank">
                            Click here for a quick guide.
                        </Link>
                    </Text>
                    <br />
                </Stack>
            </div>
        </div>
    );
}

export default AdminVideo;
